import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import { WeightUnity } from '../types/weight-unity.type';
import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_WEIGHT_UNITIES_API_PATHS } from '../constants/weight-unities-api-paths.constants';

type UseAdminGetWeightUnitiesResult = {
  isLoading: boolean;
  isRefetching: boolean;
  weightUnities: WeightUnity[];
  totalCount: number;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetWeightUnitiesResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  weightUnities: [],
  totalCount: 0,
};

const QUERY_PARAMS = { page: 1, limit: 1000, sortBy: 'name', sortDirection: 'asc' };

export function useAdminGetWeightUnities(): UseAdminGetWeightUnitiesResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<{ totalCount: number; weightUnities: WeightUnity[] }>(
        ADMIN_WEIGHT_UNITIES_API_PATHS.GET_WEIGHT_UNITIES,
        { params: QUERY_PARAMS }
      );

      setResult({
        isLoading: false,
        isRefetching: false,
        weightUnities: response.weightUnities,
        totalCount: response.totalCount,
      });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des unités de poids'),
        { variant: 'error' }
      );
      setResult({ isLoading: false, isRefetching: false, weightUnities: [], totalCount: 0 });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
